import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import {
  Shield,
  AlertTriangle,
  History,
  TrendingUp,
  Bell,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  RefreshCw,
  Loader2,
  Info,
  AlertCircle,
  X,
  ExternalLink,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { generateGovernancePdf } from '../utils/pdfGenerator';
import { apiGet, apiPost, ApiError } from '../lib/apiClient';

const Governance = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [inconsistencies, setInconsistencies] = useState(null);
  const [trends, setTrends] = useState(null);
  const [tariffImpacts, setTariffImpacts] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [skuHistory, setSkuHistory] = useState(null);
  const [searchSku, setSearchSku] = useState('');

  useEffect(() => {
    fetchGovernanceData();
  }, []);

  const fetchGovernanceData = async () => {
    setLoading(true);

    try {
      const [dashboardData, alertsData] = await Promise.all([
        apiGet('/api/governance/dashboard').catch(() => null),
        apiGet('/api/governance/alerts').catch(() => null),
      ]);

      if (dashboardData) setDashboard(dashboardData);
      if (alertsData) setAlerts(alertsData.alerts || []);
    } catch (error) {
      toast.error('Failed to load governance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchInconsistencies = async () => {
    try {
      const data = await apiGet('/api/governance/inconsistencies');
      if (data) setInconsistencies(data);
    } catch (error) {
      toast.error('Failed to load inconsistencies');
    }
  };

  const fetchTrends = async () => {
    try {
      const data = await apiGet('/api/governance/confidence-trends?days=30');
      if (data) setTrends(data);
    } catch (error) {
      toast.error('Failed to load trends');
    }
  };

  const fetchTariffImpacts = async () => {
    try {
      const data = await apiGet('/api/governance/tariff-impacts');
      if (data) setTariffImpacts(data);
    } catch (error) {
      toast.error('Failed to load tariff impacts');
    }
  };

  const fetchSkuHistory = async (skuId) => {
    try {
      const data = await apiGet(`/api/governance/history/${skuId}`);
      setSkuHistory(data);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        toast.error('SKU not found');
      } else {
        toast.error('Failed to load SKU history');
      }
      setSkuHistory(null);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await apiPost(`/api/governance/alerts/${alertId}/acknowledge`);
      setAlerts(alerts.filter(a => a.alert_id !== alertId));
      toast.success('Alert acknowledged');
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'inconsistencies' && !inconsistencies) {
      fetchInconsistencies();
    } else if (tab === 'trends' && !trends) {
      fetchTrends();
    } else if (tab === 'tariff' && !tariffImpacts) {
      fetchTariffImpacts();
    }
  };

  const exportPdf = async () => {
    try {
      const pdf = await generateGovernancePdf(dashboard, alerts, inconsistencies);
      pdf.save(`governance_report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported');
    } catch (e) {
      toast.error('Failed to generate PDF');
    }
  };

  const goToFinancialImpact = () => {
    navigate('/analyzer?tab=financial');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex">
        <Sidebar user={user} setUser={setUser} activePage="governance" />
        <main className="flex-1 p-8 lg:pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      <Sidebar user={user} setUser={setUser} activePage="governance" />
      
      <main className="flex-1 p-6 lg:p-8 lg:pt-24">
        <div className="max-w-6xl">
          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#6366F1]/10 to-[#8B5CF6]/10 text-[#6366F1] px-4 py-2 rounded-full text-sm font-semibold mb-3">
              <Shield className="w-4 h-4" />
              Classification Governance
            </div>
            <h1 className="font-outfit text-3xl font-bold text-slate-900 mb-2" data-testid="governance-title">
              Audit & Compliance
            </h1>
            <p className="text-slate-500 font-jakarta">
              Track classification history, detect inconsistencies, and monitor tariff impacts
            </p>
          </div>
          <div className="flex gap-2 mb-6">
            <Button variant="outline" size="sm" onClick={exportPdf} className="gap-2" data-testid="export-governance-pdf-btn">
              <FileText className="w-4 h-4" /> Export PDF
            </Button>
            <Button variant="outline" size="sm" onClick={goToFinancialImpact} className="gap-2" data-testid="governance-to-financial-btn">
              <DollarSign className="w-4 h-4" /> View Financial Impact
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="card-modern p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#2563EB]/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#2563EB]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{dashboard?.summary?.total_classifications || 0}</p>
                  <p className="text-xs text-slate-500">Total Classifications</p>
                </div>
              </div>
            </div>

            <div className="card-modern p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  (dashboard?.summary?.pending_alerts || 0) > 0 ? 'bg-amber-100' : 'bg-[#10B981]/10'
                }`}>
                  <Bell className={`w-5 h-5 ${
                    (dashboard?.summary?.pending_alerts || 0) > 0 ? 'text-amber-600' : 'text-[#10B981]'
                  }`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{dashboard?.summary?.pending_alerts || 0}</p>
                  <p className="text-xs text-slate-500">Pending Alerts</p>
                </div>
              </div>
            </div>

            <div className="card-modern p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  (dashboard?.summary?.inconsistencies_flagged || 0) > 0 ? 'bg-red-100' : 'bg-slate-100'
                }`}>
                  <AlertTriangle className={`w-5 h-5 ${
                    (dashboard?.summary?.inconsistencies_flagged || 0) > 0 ? 'text-red-500' : 'text-slate-400'
                  }`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{dashboard?.summary?.inconsistencies_flagged || 0}</p>
                  <p className="text-xs text-slate-500">Inconsistencies</p>
                </div>
              </div>
            </div>

            <div className="card-modern p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  (dashboard?.summary?.tariff_impacts_pending || 0) > 0 ? 'bg-purple-100' : 'bg-slate-100'
                }`}>
                  <RefreshCw className={`w-5 h-5 ${
                    (dashboard?.summary?.tariff_impacts_pending || 0) > 0 ? 'text-purple-600' : 'text-slate-400'
                  }`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{dashboard?.summary?.tariff_impacts_pending || 0}</p>
                  <p className="text-xs text-slate-500">Tariff Reviews</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-slate-200 pb-2 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: Shield },
              { id: 'history', label: 'SKU History', icon: History },
              { id: 'alerts', label: 'Alerts', icon: Bell },
              { id: 'inconsistencies', label: 'Inconsistencies', icon: AlertTriangle },
              { id: 'trends', label: 'Trends', icon: TrendingUp },
              { id: 'tariff', label: 'Tariff Schedule', icon: RefreshCw }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'bg-[#6366F1]/10 text-[#6366F1]' 
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Tariff Schedule Info */}
              <div className="card-modern p-6">
                <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-[#6366F1]" />
                  Current Tariff Schedule
                </h3>
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-600">US Schedule Version</span>
                    <span className="font-mono font-bold text-[#6366F1]">{dashboard?.tariff_schedule?.current_version}</span>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-600">Last Updated</span>
                    <span className="text-slate-900">{dashboard?.tariff_schedule?.last_updated}</span>
                  </div>
                  {dashboard?.tariff_schedule?.changes?.[0] && (
                    <div className="pt-3 border-t border-slate-200">
                      <p className="text-sm text-slate-500">Latest Change:</p>
                      <p className="text-sm text-slate-700">{dashboard?.tariff_schedule?.changes[0].description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Activity */}
              <div className="card-modern p-6">
                <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <History className="w-5 h-5 text-[#2563EB]" />
                  Recent Classifications
                </h3>
                {dashboard?.recent_activity?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.recent_activity.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[#2563EB]/10 flex items-center justify-center">
                            <FileText className="w-4 h-4 text-[#2563EB]" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{item.sku_id}</p>
                            <p className="text-xs text-slate-500">{new Date(item.timestamp).toLocaleString()}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-sm text-slate-700">{item.hs_code}</p>
                          <p className={`text-xs ${item.confidence_score >= 0.8 ? 'text-[#10B981]' : 'text-amber-500'}`}>
                            {Math.round(item.confidence_score * 100)}% confidence
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No recent activity</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="card-modern p-6">
              <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4">SKU Classification History</h3>
              
              <div className="flex gap-3 mb-6">
                <input
                  type="text"
                  placeholder="Enter SKU ID (e.g., SKU_abc123)"
                  value={searchSku}
                  onChange={(e) => setSearchSku(e.target.value)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6366F1]/20"
                  data-testid="sku-search-input"
                />
                <Button
                  onClick={() => searchSku && fetchSkuHistory(searchSku)}
                  className="bg-[#6366F1] hover:bg-[#5558E3]"
                  data-testid="sku-search-btn"
                >
                  <History className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>

              {skuHistory && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
                    <div>
                      <p className="font-semibold text-slate-900">{skuHistory.sku_id}</p>
                      <p className="text-sm text-slate-500">{skuHistory.total_versions} version(s) recorded</p>
                    </div>
                  </div>

                  {skuHistory.history?.map((record, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="bg-[#6366F1]/10 text-[#6366F1] px-2 py-1 rounded-full text-xs font-medium">
                          Version {record.version}
                        </span>
                        <span className="text-xs text-slate-500">{new Date(record.timestamp).toLocaleString()}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">HS Code</p>
                          <p className="font-mono font-medium text-slate-900">{record.hs_code}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Confidence</p>
                          <p className={`font-medium ${record.confidence_score >= 0.8 ? 'text-[#10B981]' : 'text-amber-500'}`}>
                            {Math.round(record.confidence_score * 100)}%
                            {record.confidence_change && (
                              <span className={`ml-2 text-xs ${record.confidence_change > 0 ? 'text-[#10B981]' : 'text-red-500'}`}>
                                ({record.confidence_change > 0 ? '+' : ''}{(record.confidence_change * 100).toFixed(1)}%)
                              </span>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">Source</p>
                          <p className="text-slate-700 capitalize">{record.source}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Schedule Version</p>
                          <p className="font-mono text-slate-700">{record.tariff_schedule_version}</p>
                        </div>
                      </div>

                      {record.reasoning_summary && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <p className="text-slate-500 text-xs mb-1">AI Reasoning</p>
                          <p className="text-sm text-slate-700">{record.reasoning_summary}</p>
                        </div>
                      )}

                      {record.hs_code_changed && (
                        <div className="mt-3 flex items-center gap-2 text-amber-600 text-sm">
                          <AlertCircle className="w-4 h-4" />
                          HS code changed from {record.previous_hs_code}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {!skuHistory && (
                <div className="text-center py-12 text-slate-400">
                  <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Enter a SKU ID to view its classification history</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'alerts' && (
            <div className="card-modern p-6">
              <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4">Governance Alerts</h3>
              
              {alerts.length > 0 ? (
                <div className="space-y-3">
                  {alerts.map((alert) => (
                    <div 
                      key={alert.alert_id}
                      className={`border rounded-xl p-4 ${
                        alert.severity === 'critical' ? 'border-red-200 bg-red-50' :
                        alert.severity === 'warning' ? 'border-amber-200 bg-amber-50' :
                        'border-slate-200 bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            alert.severity === 'critical' ? 'bg-red-100' :
                            alert.severity === 'warning' ? 'bg-amber-100' :
                            'bg-blue-100'
                          }`}>
                            {alert.alert_type === 'inconsistency' ? (
                              <AlertTriangle className={`w-4 h-4 ${
                                alert.severity === 'critical' ? 'text-red-600' :
                                alert.severity === 'warning' ? 'text-amber-600' :
                                'text-blue-600'
                              }`} />
                            ) : (
                              <Info className="w-4 h-4 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{alert.title}</p>
                            <p className="text-sm text-slate-600 mt-1">{alert.message}</p>
                            <p className="text-xs text-slate-400 mt-2">{new Date(alert.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => acknowledgeAlert(alert.alert_id)}
                          data-testid={`acknowledge-alert-${alert.alert_id}`}
                          className="text-slate-500 hover:text-[#10B981]"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No pending alerts</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'inconsistencies' && (
            <div className="card-modern p-6">
              <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4">Classification Inconsistencies</h3>
              
              {inconsistencies === null ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#6366F1]" />
                </div>
              ) : inconsistencies.items?.length > 0 ? (
                <div className="space-y-4">
                  <p className="text-sm text-slate-500 mb-4">
                    Found {inconsistencies.total_inconsistencies} description(s) with conflicting HS code assignments
                  </p>
                  {inconsistencies.items.map((item, idx) => (
                    <div key={idx} className="border border-amber-200 bg-amber-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-slate-900">Conflicting Classifications</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Affected SKUs</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {item.affected_skus.map((sku, i) => (
                              <span key={i} className="bg-white px-2 py-0.5 rounded text-xs font-mono">{sku}</span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-slate-500">Conflicting HS Codes</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {item.conflicting_hs_codes.map((code, i) => (
                              <span key={i} className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-mono">{code}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <Check className="w-12 h-12 mx-auto mb-3 text-[#10B981] opacity-50" />
                  <p className="text-[#10B981]">No inconsistencies detected</p>
                  <p className="text-sm mt-1">All classifications are consistent</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'trends' && (
            <div className="card-modern p-6">
              <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4">Confidence Score Trends</h3>
              
              {trends === null ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#6366F1]" />
                </div>
              ) : trends.data_points?.length > 0 ? (
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">Last {trends.period_days} days</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 px-3 text-slate-500">Date</th>
                          <th className="text-right py-2 px-3 text-slate-500">Avg Confidence</th>
                          <th className="text-right py-2 px-3 text-slate-500">Min</th>
                          <th className="text-right py-2 px-3 text-slate-500">Max</th>
                          <th className="text-right py-2 px-3 text-slate-500">Count</th>
                          <th className="text-right py-2 px-3 text-slate-500">HS Changes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trends.data_points.map((point, idx) => (
                          <tr key={idx} className="border-b border-slate-100">
                            <td className="py-2 px-3 text-slate-700">{point.date}</td>
                            <td className={`py-2 px-3 text-right font-medium ${
                              point.avg_confidence >= 0.8 ? 'text-[#10B981]' : 'text-amber-500'
                            }`}>
                              {Math.round(point.avg_confidence * 100)}%
                            </td>
                            <td className="py-2 px-3 text-right text-slate-600">{Math.round(point.min_confidence * 100)}%</td>
                            <td className="py-2 px-3 text-right text-slate-600">{Math.round(point.max_confidence * 100)}%</td>
                            <td className="py-2 px-3 text-right text-slate-600">{point.classification_count}</td>
                            <td className={`py-2 px-3 text-right ${point.hs_code_changes > 0 ? 'text-amber-500' : 'text-slate-400'}`}>
                              {point.hs_code_changes}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No trend data available yet</p>
                  <p className="text-sm mt-1">Classification history will appear here</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'tariff' && (
            <div className="card-modern p-6">
              <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4">Tariff Schedule Impacts</h3>
              
              {tariffImpacts === null ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#6366F1]" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Current Schedule */}
                  <div className="bg-[#6366F1]/5 rounded-xl p-4">
                    <h4 className="font-medium text-slate-900 mb-2">Current US Tariff Schedule</h4>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-slate-600">Version: <strong className="text-[#6366F1]">{tariffImpacts.current_schedule?.current_version}</strong></span>
                      <span className="text-slate-600">Updated: <strong>{tariffImpacts.current_schedule?.last_updated}</strong></span>
                    </div>
                  </div>

                  {/* Impacted SKUs */}
                  {tariffImpacts.impacted_skus > 0 ? (
                    <div>
                      <p className="text-amber-600 font-medium mb-3">
                        {tariffImpacts.impacted_skus} SKU(s) may need review due to tariff schedule updates
                      </p>
                      <div className="space-y-2">
                        {tariffImpacts.items?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 border border-amber-200 bg-amber-50 rounded-xl">
                            <div>
                              <p className="font-mono text-sm text-slate-900">{item.sku_id}</p>
                              <p className="text-xs text-slate-500">HS: {item.hs_code}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-slate-500">
                                Classified: v{item.classified_version} → Current: v{item.current_version}
                              </p>
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Needs Review</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <Check className="w-10 h-10 mx-auto mb-2 text-[#10B981] opacity-50" />
                      <p className="text-[#10B981]">All classifications are up to date</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Governance;
